import React, { useState, useRef, useCallback, useEffect } from 'react';
import { convertText, convertHTML, SAMPLE_TEXT } from './converters';

function HtmlGeneratorTab() {
  const [inputMode, setInputMode] = useState<'text' | 'html'>('text');
  const [textInput, setTextInput] = useState('');
  const [htmlInput, setHtmlInput] = useState('');
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [rightTab, setRightTab] = useState<'preview' | 'code'>('preview');
  const [toast, setToast] = useState('');

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const resizerRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  const doConvert = () => {
    let result = '';
    if (inputMode === 'text') {
      result = convertText(textInput);
      if (result) showToast('HTML 변환이 완료되었습니다!');
    } else {
      result = convertHTML(htmlInput);
      if (result) showToast('HTML이 쿠팡 구조로 변환되었습니다!');
    }
    setGeneratedHtml(result);
  };

  useEffect(() => {
    if (iframeRef.current && generatedHtml) {
      const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><style>body{margin:0;padding:20px;background:#fff;max-width:860px;margin:0 auto;}img{max-width:100%;height:auto;}table{border-collapse:collapse;}td{padding:8px 12px;}</style></head><body>${generatedHtml}</body></html>`);
        doc.close();
      }
    }
  }, [generatedHtml]);

  useEffect(() => {
    const resizer = resizerRef.current;
    const rightPanel = rightPanelRef.current;
    if (!resizer || !rightPanel) return;

    let startX: number, startW: number;

    const onMouseDown = (e: MouseEvent) => {
      startX = e.clientX;
      startW = rightPanel.offsetWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const onMove = (e: MouseEvent) => {
        const diff = startX - e.clientX;
        const newW = Math.max(200, Math.min(startW + diff, window.innerWidth - 500));
        rightPanel.style.width = newW + 'px';
      };
      const onUp = () => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    };

    resizer.addEventListener('mousedown', onMouseDown);
    return () => resizer.removeEventListener('mousedown', onMouseDown);
  }, []);

  const copyCode = () => {
    if (!generatedHtml) return;
    navigator.clipboard.writeText(generatedHtml).then(() => showToast('HTML 소스코드가 복사되었습니다!'));
  };

  const downloadHTML = () => {
    if (!generatedHtml) return;
    const blob = new Blob([generatedHtml], { type: 'text/html' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'coupang-detail.html';
    a.click();
    URL.revokeObjectURL(a.href);
    showToast('HTML 파일이 다운로드되었습니다!');
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Left: Input */}
      <div className="flex-1 flex flex-col min-w-[200px] overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <div className="flex gap-1 bg-white border border-gray-200 rounded-md p-0.5">
            <button
              onClick={() => setInputMode('text')}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                inputMode === 'text' ? 'bg-jeju-brown text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >텍스트 입력</button>
            <button
              onClick={() => setInputMode('html')}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                inputMode === 'html' ? 'bg-jeju-brown text-white' : 'text-gray-500 hover:text-gray-700'
              }`}
            >HTML 입력</button>
          </div>
          <button
            onClick={() => { setTextInput(SAMPLE_TEXT); setInputMode('text'); }}
            className="px-3 py-1.5 border border-gray-300 bg-white text-gray-600 rounded-md text-xs font-medium hover:bg-gray-50 transition-colors"
          >예시 불러오기</button>
        </div>
        <div className="flex-1 overflow-hidden">
          <textarea
            value={inputMode === 'text' ? textInput : htmlInput}
            onChange={(e) => inputMode === 'text' ? setTextInput(e.target.value) : setHtmlInput(e.target.value)}
            placeholder={inputMode === 'text'
              ? '스마트스토어, 블로그 등에서 복사한 상품 상세 텍스트를 여기에 붙여넣어 주세요.\n\n규칙:\n• 빈 줄 2개 이상 → 섹션 구분\n• "큰따옴표" → 하이라이트 인용문\n• ==텍스트== → 노란 하이라이트\n• ~~텍스트~~ → 취소선\n• [이미지] → 이미지 자리표시\n• ▶ → 굵은 텍스트'
              : '다른 쇼핑몰의 상품 상세 HTML 소스코드를 여기에 붙여넣어 주세요.'}
            className="w-full h-full bg-white border-none text-gray-800 p-5 text-sm leading-relaxed resize-none focus:outline-none placeholder-gray-400"
          />
        </div>
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-center">
          <button
            onClick={doConvert}
            className="px-10 py-2.5 text-sm font-bold bg-jeju-brown text-white rounded-lg hover:bg-jeju-brown-dark transition-colors"
          >변환하기</button>
        </div>
      </div>

      {/* Resizer */}
      <div
        ref={resizerRef}
        className="w-1.5 cursor-col-resize bg-gray-200 hover:bg-jeju-brown transition-colors flex-shrink-0"
      />

      {/* Right: Preview/Code */}
      <div ref={rightPanelRef} className="w-[500px] flex flex-col flex-shrink-0 min-w-[200px] overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setRightTab('preview')}
            className={`flex-1 py-2.5 text-center text-sm font-semibold transition-colors border-b-2 ${
              rightTab === 'preview'
                ? 'text-jeju-brown border-jeju-brown'
                : 'text-gray-400 border-transparent hover:text-gray-600'
            }`}
          >미리보기</button>
          <button
            onClick={() => setRightTab('code')}
            className={`flex-1 py-2.5 text-center text-sm font-semibold transition-colors border-b-2 ${
              rightTab === 'code'
                ? 'text-jeju-brown border-jeju-brown'
                : 'text-gray-400 border-transparent hover:text-gray-600'
            }`}
          >소스코드</button>
        </div>
        <div className="flex-1 overflow-hidden relative">
          <iframe
            ref={iframeRef}
            title="preview"
            className="w-full h-full border-none bg-white"
            style={{ display: rightTab === 'preview' ? 'block' : 'none' }}
          />
          <div
            className="w-full h-full overflow-auto p-4 bg-gray-900"
            style={{ display: rightTab === 'code' ? 'block' : 'none' }}
          >
            <div className="sticky top-0 bg-gray-900 pb-2 z-10 flex justify-end gap-2">
              <button onClick={copyCode} className="px-3 py-1.5 text-xs bg-jeju-brown text-white rounded-md hover:bg-jeju-brown-dark transition-colors">복사하기</button>
              <button onClick={downloadHTML} className="px-3 py-1.5 text-xs border border-gray-600 text-gray-300 rounded-md hover:bg-gray-800 transition-colors">다운로드</button>
            </div>
            <pre className="font-mono text-xs leading-relaxed text-gray-300 whitespace-pre-wrap break-all">
              {generatedHtml}
            </pre>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-jeju-brown text-white px-6 py-2.5 rounded-lg text-sm font-semibold z-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

export default HtmlGeneratorTab;
